/**
 * "Arrived from the recommendation section" attribution (P1D-5464 FR-41/42).
 * Written when a recommended card, its "View rooms" CTA or its map pin opens a property page;
 * read by the route page-view tracker. Public ids and filter keys/values only — no user data.
 * Ignored after 30 minutes.
 *
 * Kept in localStorage, not sessionStorage: desktop cards open the property in a new tab
 * (`target="_blank"`, no opener), which starts with an empty sessionStorage (P1D-5538 TC-P22).
 * The record is consumed by the first matching page view, so a later visit to the same property
 * is not attributed again.
 */
const KEY = "search_recommendation_click";
const TTL_MS = 30 * 60 * 1000;
export const SEARCH_RECOMMENDATIONS_SOURCE = "search_recommendations";

export type SearchRecommendationClick = {
  canonical_name: string;
  step_index: number;
  /**
   * Removed option tokens (e.g. "amenities:gym"). Records written by an interim P1D-5538 build
   * hold group ids here instead; they are passed through as-is.
   */
  removed_filters: string[];
  /** Removed group ids (e.g. "meta_types"), in removal order. Absent in older records. */
  removed_filter_groups?: string[];
  /** P1D-5538: absent in records written before it. */
  month_widened?: boolean;
  /** The link's filter query params (`partner_id` excluded). */
  passed_filters?: Record<string, string>;
};

export const setSearchRecommendationAttribution = (data: SearchRecommendationClick) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ ...data, ts: Date.now() }));
  } catch {
    // storage unavailable — attribution is best-effort
  }
};

const clearRecord = () => {
  try {
    window.localStorage.removeItem(KEY);
    // Records written by builds before TC-P22 live in sessionStorage.
    window.sessionStorage.removeItem(KEY);
  } catch {
    // storage unavailable
  }
};

/**
 * Props to merge into a property page view event, or null when it does not apply. A matching
 * record is removed once read.
 */
export const getSearchRecommendationAttribution = (pathname: string) => {
  try {
    const raw = window.localStorage.getItem(KEY) ?? window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as SearchRecommendationClick & { ts: number };
    if (!data?.canonical_name || Date.now() - data.ts > TTL_MS) {
      clearRecord();
      return null;
    }
    if (!pathname.replace(/\/$/, "").endsWith(`/places/${data.canonical_name}`)) return null;
    clearRecord();
    // Older records lack the newer fields: omit them rather than send undefined.
    return {
      source: SEARCH_RECOMMENDATIONS_SOURCE,
      recommendation_step_index: data.step_index,
      recommendation_removed_filters: Array.isArray(data.removed_filters)
        ? data.removed_filters
        : [],
      opened_from_recommendation: true,
      ...(Array.isArray(data.removed_filter_groups) && {
        recommendation_removed_filter_groups: data.removed_filter_groups,
      }),
      ...(typeof data.month_widened === "boolean" && {
        recommendation_month_widened: data.month_widened,
      }),
      ...(data.passed_filters &&
        typeof data.passed_filters === "object" && {
          recommendation_passed_filters: data.passed_filters,
        }),
    };
  } catch {
    return null;
  }
};
